import { useQuery } from "@tanstack/react-query";
import Link from "next/link";

import Layout from "../components/layouts/layout.comp";
import LocalePs from "../components/locale/localePs.core";
import { ListSection, Tag } from "../components/reuseables/list.comp";
import attributeCore from "../core/attribute.core";
import localeCore from "../core/locale.core";
import { useTranslation } from "../core/translation.core";
import ErrorCenterIcon from "../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../icons/loadingOvalCenter.icon";
import { Attribute } from "../types/attribute";
import AddLink from "../components/form/addLink.comp";

export default function AttributesPage() {
  const { t, msg } = useTranslation();

  return (
    <Layout title={t(msg.attribute.attribute)} titleBtn={<AddLink id="attribute-create" href="/attribute/create" />}>
      <ListSection>
        <List />
      </ListSection>
    </Layout>
  );
}

const List = () => {
  const { t, msg } = useTranslation();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["attributes"],
    queryFn: attributeCore.listAttributes,
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isLoading) return <LoadingOvalCenterIcon />;

  return (
    <div id="attribute-list" className="pt-6 lg:pt-0">
      <div className="hidden lg:grid lg:grid-cols-[6fr_3fr_3fr_3fr_2fr] gap-6 px-6 py-3 text-sm font-medium">
        <div>{t(msg.attribute.name)}</div>
        <div>{t(msg.attribute.type)}</div>
        <div>{t(msg.attribute.unit)}</div>
        <div>{t(msg.attribute.display)}</div>
        <div></div>
      </div>

      {data!.map((a) => (
        <Row key={a.id} attribute={a} />
      ))}
    </div>
  );
};

function Row({ attribute }: { attribute: Attribute }) {
  const { t, msg } = useTranslation();

  return (
    <div className="attribute-row grid grid-cols-2 lg:grid-cols-[6fr_3fr_3fr_3fr_2fr] gap-x-6 gap-y-3 px-6 py-6 border-t-[1px] text-sm group/edit">
      <div data-value={localeCore.getLocaleValueDefault(attribute.name)}>
        <Tag>{t(msg.attribute.name)}</Tag>
        <LocalePs value={attribute.name} />
      </div>
      <div>
        <Tag>{t(msg.attribute.type)}</Tag> {attribute.type}
      </div>
      <div data-value={localeCore.getLocaleValueDefault(attribute.unit)}>
        <Tag>{t(msg.attribute.unit)}</Tag>
        <LocalePs value={attribute.unit} />
      </div>
      <div>
        <Tag>{t(msg.attribute.display)}</Tag>
        <DisplayType attribute={attribute} />
      </div>
      <div className="col-span-2 lg:col-span-1 opacity-0 group-hover/edit:opacity-100">
        <Link className="attribute-edit underline" href={`/attribute/edit/${attribute.id}`}>
          {t(msg.form.edit)}
        </Link>
      </div>
    </div>
  );
}

function DisplayType({ attribute }: { attribute: Attribute }) {
  const { t, msg } = useTranslation();

  switch (attribute.display) {
    case "all":
      return <div>{t(msg.attribute.typeAll)}</div>;
    case "none":
      return <div>{t(msg.attribute.typeNone)}</div>;
    default:
      return <div></div>;
  }
}
